import { ArrowRightOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Modal,
  ModalProps,
  Row,
  Select,
  Tag
} from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { useEffect } from 'react'

import {
  useCounterpartServiceGetInternalInstitutionEndpoint,
  usePersonsServiceCreatePersonTransferRequestEndpoint,
  useTreatmentServiceGetTreatmentPlanEndpoint,
  useTreatmentServiceLatestConsultationEndpoint
} from 'src/api/queries'
import {
  ApiError,
  GetPersonByIdnpResponse,
  PersonTransferType
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  InternalInstitutionsSelect,
  ModalFooterProps,
  SaveConfirm,
  Text
} from 'src/components'
import { useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { useStyles } from '../styles'

type Props = {
  data?: GetPersonByIdnpResponse
  onCancel?: () => void
} & ModalProps

const Footer = ({ onCancel, onSubmit }: ModalFooterProps) => {
  const { t } = useLocales()

  return (
    <Flex align="center" justify="center" gap={8}>
      <Button onClick={onCancel}>{t('common.cancel')}</Button>

      <SaveConfirm title={`${t('common.confirm')}?`} onConfirm={onSubmit}>
        <Button type="primary">{t('common.confirm')}</Button>
      </SaveConfirm>
    </Flex>
  )
}

export const TransferModal = ({ data, onCancel }: Props) => {
  const { t, currentLang } = useLocales()
  const { styles } = useStyles()

  const [form] = Form.useForm()

  const { data: consultationDetails } =
    useTreatmentServiceLatestConsultationEndpoint({
      personId: String(data?.id)
    })

  const { userData } = useUser()

  const { data: userInstitution } =
    useCounterpartServiceGetInternalInstitutionEndpoint(
      { id: String(userData?.currentLegalEntityId) },
      [String(userData?.currentLegalEntityId)],
      { enabled: !!userData?.currentLegalEntityId }
    )

  const { data: treatmentDetails } =
    useTreatmentServiceGetTreatmentPlanEndpoint(
      {
        id: String(consultationDetails?.treatmentPlanId)
      },
      [String(consultationDetails?.treatmentPlanId)],
      {
        enabled: !!consultationDetails?.treatmentPlanId
      }
    )

  const isPatientAtUserInstitution =
    data?.institution?.id === userData?.currentLegalEntityId

  const { mutate, isPending } =
    usePersonsServiceCreatePersonTransferRequestEndpoint({
      onSuccess: () => {
        onCancel && onCancel()
      },
      onError: (e: ApiError) => formErrorHandler(e.body.errors)
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    mutate({
      requestBody: {
        personId: String(data?.id),
        destinationInstitutionId: values.destinationInstitutionId,
        notes: values.notes,
        transferType: values.transferType
      }
    })
  }

  useEffect(() => {
    const updates: {
      currentInstitutionId?: string
      destinationInstitutionId?: string
    } = {}
    if (data?.institution?.id) {
      updates.currentInstitutionId = data.institution.id
    }
    if (!isPatientAtUserInstitution && userInstitution?.id) {
      updates.destinationInstitutionId = userInstitution.id
    }
    if (Object.keys(updates).length) {
      form.setFieldsValue(updates)
    }
  }, [
    data?.institution?.id,
    data?.institution?.name,
    userInstitution,
    isPatientAtUserInstitution
  ])

  return (
    <Modal
      title={t('transfer.title')}
      open
      maskClosable={false}
      footer={Footer({
        onCancel,
        onSubmit: () => {
          void onSubmit()
        }
      })}
      okButtonProps={{ loading: isPending }}
      width={1000}
      onCancel={onCancel}
    >
      <Flex vertical gap={24}>
        <Card title={t('common.person')}>
          <Row gutter={24} align="middle">
            <Col>
              <Flex gap={8} align="center">
                <Text weight={400}>{t('form.last_name')}:</Text>
                <Text weight={700}>
                  {data?.firstName} {data?.lastName}
                </Text>
                {treatmentDetails?.treatmentType && (
                  <Tag className={styles.treatmentTypeTag}>
                    {
                      treatmentDetails?.treatmentType?.translation?.[
                        currentLang?.value
                      ]
                    }
                  </Tag>
                )}
              </Flex>
            </Col>

            <Col>
              <Flex gap={8}>
                <Text weight={400}>{t('common.phone')}:</Text>
                <Text weight={700}>{data?.phone ? data?.phone : '-'}</Text>
              </Flex>
            </Col>

            <Col>
              <Flex gap={8}>
                <Text weight={400}>{t('cases.email_address')}:</Text>
                <Text weight={700} ellipsis>
                  {data?.email ? data?.email : '-'}
                </Text>
              </Flex>
            </Col>
          </Row>
        </Card>

        <Form
          requiredMark={false}
          layout="vertical"
          form={form}
          initialValues={{ transferType: PersonTransferType.TEMPORARY }}
        >
          <Row gutter={24} align="bottom">
            <Col span={11}>
              <Form.Item
                name="currentInstitutionId"
                label={t('transfer.from_institution')}
              >
                <InternalInstitutionsSelect
                  placeholder={t('transfer.from_institution')}
                  disabled
                  initialOptions={
                    data?.institution?.id && data?.institution?.name
                      ? [
                          {
                            value: data?.institution?.id,
                            label: data?.institution?.name
                          }
                        ]
                      : undefined
                  }
                />
              </Form.Item>
            </Col>

            <Col span={2}>
              <Flex justify="center">
                <Button icon={<ArrowRightOutlined />} />
              </Flex>
            </Col>

            <Col span={11}>
              <Form.Item
                name="destinationInstitutionId"
                label={t('transfer.to_institution')}
                rules={requiredRules}
              >
                <InternalInstitutionsSelect
                  excludeIds={[data?.institution?.id ?? '']}
                  placeholder={t('transfer.to_institution')}
                  disabled={!isPatientAtUserInstitution}
                  initialOptions={
                    !isPatientAtUserInstitution &&
                    userInstitution?.id &&
                    userInstitution?.name
                      ? [
                          {
                            value: userInstitution.id,
                            label: userInstitution.name
                          }
                        ]
                      : undefined
                  }
                />
              </Form.Item>
            </Col>

            <Divider />
            <Flex vertical gap={8} className="full-width">
              <Col span={24}>
                <Form.Item
                  name="notes"
                  label={t('transfers.transfer_patient_reason')}
                >
                  <TextArea
                    placeholder={t('transfers.transfer_patient_reason')}
                    rows={4}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="transferType"
                  label={t('transfer.transfer_type')}
                  rules={requiredRules}
                >
                  <Select
                    placeholder={t('transfer.transfer_type')}
                    options={Object.values(PersonTransferType).map((type) => ({
                      label: t(`transfer.${type}`),
                      value: type
                    }))}
                  />
                </Form.Item>
              </Col>
            </Flex>
          </Row>
        </Form>
      </Flex>
    </Modal>
  )
}
